/**
 * namedEffectGenerator (T3.1) — realize every TOML cast_effect/impact_effect name.
 *
 * Most ability VFX names authored in TOML were never registered effects, so they
 * silently fell to a generic particle burst (or the ABILITY_VFX_MAP fallback). DJ's
 * rule: "if we named these things, there should be corresponding data." This gives
 * EVERY named effect a registered VFXEffectConfig:
 *   - alias to the curated ABILITY_VFX_MAP effect when one exists (same look as today)
 *   - else a school-derived burst recolored to the ability's school
 *
 * Runs once at VFXSystem boot, after the static effect arrays register. Pure planning
 * (collect/plan/build) is unit-tested; registerNamedAbilityEffects wires it to the registry.
 */

import { VFXEffectRegistry, type VFXEffectConfig } from '../VFXEffectRegistry';
import { IMPACT_VFX_CONFIGS, CAST_VFX_CONFIGS } from '../../systems/player/abilities/AbilityPresets';
import { ABILITY_SCHOOL_COLORS } from '../../../shared/constants/AbilitySchoolColors';
import { hexToNumber } from '../abilityPalette';
import { SCHOOL_PARTICLE_BURST } from './ParticleEffects';
import { ABILITY_DATA } from '../../../shared/content/generated/abilities.gen';
import { getAbilityVFX } from './AbilityVFXMap';
import { gameLogger } from '../../../utils/GameLogger';
import { resolveAbilityArchetype, ABILITY_VFX_ARCHETYPES, ENABLE_ARCHETYPE_CAST_FALLBACK, archCastId, archImpactId } from '../../../config/abilities/vfxArchetypes.config';
import { buildArchetypeEffectConfig } from './archetypeEffectBuilder';
import { pulsingShell, orbiter } from './abilities/statusAuraBuilders';

export interface NamedEffectReq {
  name: string;
  field: 'cast' | 'impact';
  school: string;
  abilityId: string;
  /** Ability's class — needed to look up the (class, school) archetype. */
  cls: string;
  /** The ability's own authored color (F5-015) — overrides the school/family
   *  palette so authored intent (eviscerate #cc0000) survives realization. */
  color?: number;
  /** Buff cast effects (F6 shaman lane): realize as a PERSISTENT aura that
   *  lives with the buff, not a one-shot burst that flashes and vanishes. */
  persistent?: boolean;
}

export type NamedEffectPlan = { kind: 'alias'; target: string } | { kind: 'school' };

/**
 * Generic cast targets an authored name must NOT alias to (cast-side doctrine
 * §4, 2026-07-17): `casting_glow` is the one-size 30px breathing circle — an
 * authored "summon_beast" wearing it is laundering, not curation. Gated names
 * realize through the archetype/school tier instead (family palette + authored
 * color — strictly richer). Deliberately narrow: class-flavored crafted
 * targets (`sword_swing`, `shadow_strike`, `haste`, …) stay aliasable; if a
 * later art pass condemns more, add them here — the mechanism is ready.
 */
const GENERIC_CAST_TARGETS = new Set(['casting_glow']);

/** Impact preset (from AbilityPresets) per school — recolored to the school below. */
const SCHOOL_IMPACT_PRESET: Record<string, keyof typeof IMPACT_VFX_CONFIGS> = {
  fire: 'burn', chaos: 'explosion', holy: 'explosion',
  frost: 'freeze', ice: 'freeze',
  arcane: 'spark', shadow: 'spark', nature: 'spark', physical: 'spark',
  lightning: 'spark', healing: 'spark', various: 'spark',
};

interface AbilityVfxLike {
  school?: string;
  class?: string;
  abilityType?: string;
  vfx?: { castEffect?: string; impactEffect?: string; projectileColor?: string; auraColor?: string };
}

/**
 * The ability's own TOML-authored color, most-specific first: projectile color
 * (the effect's own hue), then aura color. Undefined when the TOML authors no
 * color — the school/archetype palette is then the honest fallback, not a
 * discard (F5-015). Deliberately does NOT read the authored `visual` palette: per
 * the vfxArchetypes contract, family palettes own the fallback tier and only a
 * per-ability authored color overrides them.
 */
export function authoredEffectColor(ab: AbilityVfxLike): number | undefined {
  const hex = ab.vfx?.projectileColor ?? ab.vfx?.auraColor;
  if (!hex) return undefined;
  return hexToNumber(hex) ?? undefined;
}

/** Distinct unregistered cast/impact names across all abilities (first ability wins for school). */
export function collectNamedEffectReqs(
  abilities: Record<string, AbilityVfxLike>,
  isRegistered: (id: string) => boolean,
): NamedEffectReq[] {
  const seen = new Set<string>();
  const reqs: NamedEffectReq[] = [];
  const add = (name: string | undefined, field: 'cast' | 'impact', school: string, abilityId: string, cls: string, color?: number, persistent?: boolean) => {
    if (!name || seen.has(name) || isRegistered(name)) return;
    seen.add(name);
    reqs.push({ name, field, school, abilityId, cls, color, persistent });
  };
  for (const [abilityId, ab] of Object.entries(abilities)) {
    const school = ab.school || 'physical';
    const cls = ab.class || '';
    const color = authoredEffectColor(ab);
    // Buff cast effects live for the buff's DURATION — realizing them as
    // one-shot bursts made every authored imbue (rockbiter_enchant,
    // flametongue_enchant, lightning_shield, bloodlust_aura …) flash for
    // ~400ms then run invisible for the rest of the buff (F6 shaman lane).
    const persistentCast = ab.abilityType === 'buff';
    add(ab.vfx?.castEffect, 'cast', school, abilityId, cls, color, persistentCast);
    add(ab.vfx?.impactEffect, 'impact', school, abilityId, cls, color);
  }
  return reqs;
}

export function planNamedEffect(
  req: NamedEffectReq,
  isRegistered: (id: string) => boolean,
  getMap: (abilityId: string) => { castVfxId?: string; hitVfxId?: string } | undefined,
): NamedEffectPlan {
  const m = getMap(req.abilityId);
  const target = req.field === 'cast' ? m?.castVfxId : m?.hitVfxId;
  // Aliasing to the generic school family is NOT curation (wave-2 doctrine):
  // an authored 'spark'/'fire_burst' aliased to hit_* just wears the generic
  // flash under a new name; same for the cast side's one generic glow
  // (GENERIC_CAST_TARGETS). The archetype/school tier below is richer (family
  // palette + authored color + school particles) — reserve aliases for
  // genuinely bespoke targets (deep_wound→rupture, meteor_warning→meteor_charge
  // stay useful).
  if (target && isRegistered(target) && !target.startsWith('hit_') && !GENERIC_CAST_TARGETS.has(target)) {
    return { kind: 'alias', target };
  }
  return { kind: 'school' };
}

/** A registered burst config under `name` — tinted to the ability's authored
 *  color when one exists (F5-015), else recolored to the school's color.
 *  Impacts also carry the school's particle burst (SCHOOL_PARTICLE_BURST,
 *  2026-07-16 expansion) so generated effects read as particles + shapes, not
 *  shape fallbacks only. */
export function buildSchoolEffect(name: string, field: 'cast' | 'impact', school: string, authoredColor?: number): VFXEffectConfig {
  const color = authoredColor ?? ABILITY_SCHOOL_COLORS[school] ?? ABILITY_SCHOOL_COLORS.physical;
  const base = field === 'impact'
    ? (IMPACT_VFX_CONFIGS[SCHOOL_IMPACT_PRESET[school] ?? 'spark'] ?? IMPACT_VFX_CONFIGS.spark)
    : CAST_VFX_CONFIGS.glow;
  const primitives: VFXEffectConfig['primitives'] = base.map((p) => ({ ...p, color }));
  if (field === 'impact') {
    const particleId = SCHOOL_PARTICLE_BURST[school];
    if (particleId) {
      primitives.unshift({ type: 'particles', particleEffectId: particleId, particleCount: 7 });
    }
  }
  return {
    id: name,
    name: `${name} (auto ${school} ${field})`,
    category: 'burst',
    primitives,
  };
}

/** A registered PERSISTENT aura config under `name` — for buff cast effects
 *  that live as long as the buff (F6 shaman lane: rockbiter_enchant et al.
 *  realized as bursts flashed once then ran invisible for the duration).
 *  School/authored-colored shell + orbiting motes, all repeat -1, following
 *  the buffed entity. */
export function buildStatusAuraEffect(name: string, school: string, authoredColor?: number): VFXEffectConfig {
  const color = authoredColor ?? ABILITY_SCHOOL_COLORS[school] ?? ABILITY_SCHOOL_COLORS.physical;
  return {
    id: name,
    name: `${name} (auto ${school} status aura)`,
    category: 'status',
    followTarget: 'player',
    // Same grammar as the bespoke status auras (statusAuraBuilders) —
    // breathing shell + counter-rotating motes in the school/authored color.
    primitives: [
      pulsingShell({
        width: 54, height: 64, color, alpha: 0.18, glow: true,
        stroke: { color, width: 2 },
        pulse: { scale: 1.08, alpha: 0.12, duration: 700 },
      }),
      orbiter({ radius: 3, color, alpha: 0.85, glow: true, orbit: { radius: 28, speed: 0.4 } }),
      orbiter({ radius: 2, color: 0xFFFFFF, alpha: 0.6, orbit: { radius: 22, speed: -0.55, startAngle: Math.PI } }),
    ],
  };
}

/**
 * Register a corresponding effect for every named cast/impact effect that lacks one.
 * Idempotent (skips already-registered names). Call after the static effect arrays load.
 *
 * Resolution for the school-derived tier (no bespoke alias, no map entry):
 *   1. If the ability's (class, school) has a VFX archetype → build from the
 *      archetype's palette + motion signature (family-coherent look).
 *   2. Else fall back to the generic school-derived burst (buildSchoolEffect).
 */
export function registerNamedAbilityEffects(): void {
  const isRegistered = (id: string) => VFXEffectRegistry.has(id);
  const reqs = collectNamedEffectReqs(ABILITY_DATA as unknown as Record<string, AbilityVfxLike>, isRegistered);
  let aliased = 0;
  let derived = 0;
  let archetypeDerived = 0;
  for (const req of reqs) {
    // Persistent (buff) cast effects realize as long-lived auras BEFORE any
    // alias/archetype burst plan — a burst-shaped realization is wrong for
    // them regardless of what it aliases (F6 shaman lane).
    if (req.persistent) {
      VFXEffectRegistry.register(buildStatusAuraEffect(req.name, req.school, req.color));
      derived += 1;
      continue;
    }
    const plan = planNamedEffect(req, isRegistered, getAbilityVFX);
    if (plan.kind === 'alias') {
      const src = VFXEffectRegistry.get(plan.target);
      if (src) {
        VFXEffectRegistry.register({ ...src, id: req.name, name: `${req.name} (→ ${plan.target})` });
        aliased += 1;
        continue;
      }
    }
    // School-derived tier: prefer archetype (family palette + motion) when available.
    const archetype = resolveAbilityArchetype(req.cls, req.school);
    if (archetype) {
      VFXEffectRegistry.register(buildArchetypeEffectConfig(req.name, req.field, archetype, req.color));
      archetypeDerived += 1;
    } else {
      VFXEffectRegistry.register(buildSchoolEffect(req.name, req.field, req.school, req.color));
      derived += 1;
    }
  }
  // Register the archetype fallback cast/impact ids for every class+school.
  // These are referenced by metadata.vfx.cast when ENABLE_ARCHETYPE_CAST_FALLBACK
  // is on and a TOML ability does not author its own cast_effect.
  if (ENABLE_ARCHETYPE_CAST_FALLBACK) {
    for (const archetype of Object.values(ABILITY_VFX_ARCHETYPES)) {
      const castId = archCastId(archetype.cls, archetype.school);
      if (!isRegistered(castId)) {
        VFXEffectRegistry.register(buildArchetypeEffectConfig(castId, 'cast', archetype));
        archetypeDerived += 1;
      }
      const impactId = archImpactId(archetype.cls, archetype.school);
      if (!isRegistered(impactId)) {
        VFXEffectRegistry.register(buildArchetypeEffectConfig(impactId, 'impact', archetype));
        archetypeDerived += 1;
      }
    }
  }

  gameLogger.info('vfx', `[namedEffects] realized ${reqs.length} named effects (${aliased} aliased, ${archetypeDerived} archetype-derived, ${derived} school-derived)`);
}
